import {Injectable} from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpErrorResponse, HttpResponse
} from '@angular/common/http';
import {Observable, from, tap} from 'rxjs';
import {switchMap, catchError} from 'rxjs/operators';
import {AuthService} from "@services";
import {Router} from "@angular/router";
import notify from "devextreme/ui/notify";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    constructor(private auth: AuthService, private router: Router) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = this.auth.getAccessToken();
        const cloned = token ? req.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        }) : req;

        return next.handle(cloned).pipe(
            tap(async event => {
                if (event instanceof HttpResponse) {
                    const location = event.headers.get('Location');
                    if (location) {
                        await this.router.navigateByUrl(location);
                    }
                }
            }),

            catchError((err: HttpErrorResponse) => {
                // If 401 received, try to send the request again.
                if (err.status === 401) {
                    return from(this.auth.refresh()).pipe(
                        catchError(async (refreshErr) => {
                            await this.redirectToLogin();
                            throw refreshErr;
                        }),
                        switchMap(() => {
                            const newToken = this.auth.getAccessToken();
                            const retryReq = newToken ? req.clone({
                                setHeaders: {
                                    Authorization: `Bearer ${newToken}`
                                }
                            }) : req;

                            return next.handle(retryReq).pipe(
                                catchError(async (retryErr) => {
                                    if (retryErr.status === 401) {
                                        await this.redirectToLogin();
                                    }
                                    throw retryErr;
                                })
                            );
                        })
                    );
                }

                if (err.status == 403) {
                    notify({
                        message: `Access denied. You do not have permission to access this resource. ${err.message}`,
                        position: 'bottom center'
                    }, 'error', 3000);
                }

                if (err.error instanceof Blob) {
                    // Handle Blob error response
                    return new Observable<HttpEvent<any>>((observer) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            const errorText = reader.result as string;
                            try {
                                const parsed = errorText ? JSON.parse(errorText) : null;
                                observer.error(parsed);
                            } catch {
                                observer.error('Can\'t parse error response');
                            }
                        };
                        reader.onerror = () => {
                            observer.error(new Error('Failed to read error response'));
                        };
                        reader.readAsText(err.error);
                    });
                } else {
                    let message = err.error ? err.error.map(e => e.message).join('\n') : err.message;
                    throw new Error(message);
                }
            })
        );
    }

    private async redirectToLogin() {
        await this.router.navigate(['/auth/sign-in'], {queryParams: {ReturnUrl: window.location.pathname}});
    }
}
